import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import PhotoCameraIcon from '@mui/icons-material/PhotoCameraOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import { useT } from '../../i18n';
import { newRequestId, uploadPhoto, UploadedAttachment } from './photoUpload';

interface Props {
  ownerType: string;
  ownerRef: string;
  routeId?: number | null;
  required?: boolean;
  onUploaded?: (attachment: UploadedAttachment) => void;
  onError?: (message: string) => void;
  dataTour?: string;
}

/** Botón «Foto del soporte»: abre la cámara del celular o el selector de archivos y sube la foto a R2. */
const PhotoUploadButton: React.FC<Props> = ({ ownerType, ownerRef, routeId, required, onUploaded, onError, dataTour }) => {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const requestId = useRef(newRequestId('photo'));
  const [state, setState] = useState<'idle' | 'uploading' | 'done'>('idle');

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setState('uploading');
    try {
      const attachment = await uploadPhoto(file, { ownerType, ownerRef, routeId, kind: 'receipt' }, requestId.current);
      setState('done');
      onUploaded?.(attachment);
    } catch (error: any) {
      setState('idle');
      requestId.current = newRequestId('photo');
      // Espacio lleno hace 30 días: el texto en el idioma de la pantalla.
      onError?.(error?.response?.data?.code === 'STORAGE_FULL' ? t('attachments.storageFull') : error?.response?.data?.message || t('attachments.uploadError'));
    } finally {
      if (input.current) input.current.value = '';
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }} data-tour={dataTour}>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => void onFile(e.target.files?.[0])}
      />
      {state === 'done' ? (
        <Chip size="small" color="success" icon={<CheckCircleIcon />} label={t('attachments.photoReady')} onClick={() => input.current?.click()} />
      ) : (
        <Button
          size="small"
          variant={required ? 'contained' : 'outlined'}
          color={required ? 'warning' : 'primary'}
          startIcon={state === 'uploading' ? <CircularProgress size={14} color="inherit" /> : <PhotoCameraIcon />}
          disabled={state === 'uploading'}
          onClick={() => input.current?.click()}
        >
          {state === 'uploading' ? t('attachments.uploading') : required ? t('attachments.addPhotoRequired') : t('attachments.addPhoto')}
        </Button>
      )}
    </Box>
  );
};

export default PhotoUploadButton;
